import React, {useState, useEffect, useCallback, useRef} from 'react'
import {useSelector, useDispatch} from 'react-redux'
import {KPSEOAddonWrapperStyle, StepperWrapper} from './kp-seo-addons-style'
import {
  selectAlphaKeyCount,
  selectAlphaPowerBoostCount,
  setAlphaPowerBoostCount,
  setAlphaKeyCount,
} from '../../../../app-store/kp11CartSlice'

/**
 * Get SEO addon price from currencies array based on selected country
 * @param {Array} currencies - Array of currency objects from backend
 * @param {string} selectedCountry - Country code (US, CA, AU, NZ)
 * @returns {number} Price for the selected country
 */
const getPriceFromCurrencies = (currencies, selectedCountry) => {
  if (!currencies || !Array.isArray(currencies)) {
    // Fallback prices if currencies array is missing
    const fallbackPrices = {
      US: 400,
      CA: 500,
      AU: 600,
      NZ: 700,
    }
    return fallbackPrices[selectedCountry] || 400
  }

  const currencyMap = {
    US: 'USD',
    CA: 'CAD',
    AU: 'AUD',
    NZ: 'NZD',
    GB: 'GBP',
  }

  const targetCurrency = currencyMap[selectedCountry] || 'USD'
  const currencyObj = currencies.find(c => c.currency === targetCurrency)

  return currencyObj?.unitAmount || 400
}

const KPSEOStepper = ({
  label,
  value = 0,
  min = 0,
  max = 3,
  step = 1,
  onChange,
  unitLabel,
  disabled = false,
  defaultDisableValue,
}) => {
  const handleDecrement = useCallback(() => {
    if (value > min && !disabled) onChange(value - step)
  }, [value, min, step, disabled, onChange])

  const handleIncrement = useCallback(() => {
    if (value < max && !disabled) onChange(value + step)
  }, [value, max, step, disabled, onChange])

  return (
    <StepperWrapper className={disabled ? 'disabled' : ''}>
      {label && <label className="stepper-label">{label}</label>}

      <div className="stepper-box">
        <button
          type="button"
          className="step-btn minus"
          onClick={handleDecrement}
          disabled={disabled || value <= defaultDisableValue || value <= min}
        >
          –
        </button>

        <div className="step-value">{value}</div>

        <button
          type="button"
          className="step-btn plus"
          onClick={handleIncrement}
          disabled={disabled || value >= max}
        >
          +
        </button>
      </div>

      {unitLabel && <span className="unit-label">{unitLabel}</span>}
    </StepperWrapper>
  )
}

export const KPSEOAddons = ({
  selectedDefaultItem,
  selectedItem,
  onSEOAddonChange,
  disabled,
  selectedCountry = 'US',
  seoEntitlements = [],
}) => {
  const dispatch = useDispatch()

  const alphaKeyCount = useSelector(selectAlphaKeyCount)
  const alphaPowerboostCount = useSelector(selectAlphaPowerBoostCount)
  const selectedAddons = selectedItem?.addons || []
  const selectedDefaultAddons = selectedDefaultItem?.addons || []

  // Get pricing from backend entitlements data
  const seoPowerboostEnt = seoEntitlements.find(
    e => e.code === 'addon-5-seopb-m2m',
  )
  const seoKeywordEnt = seoEntitlements.find(
    e => e.code === 'addon-5-seokey-m2m',
  )

  const powerboostPrice = getPriceFromCurrencies(
    seoPowerboostEnt?.currencies,
    selectedCountry,
  )
  const keywordPrice = getPriceFromCurrencies(
    seoKeywordEnt?.currencies,
    selectedCountry,
  )

  // Default structure for addons (if missing)
  const defaultAddons = {
    'addon-5-seopb-m2m': {
      code: 'addon-5-seopb-m2m',
      name: 'AEO & SEO PowerBoost',
      unitAmount: powerboostPrice,
      quantity: 0,
    },
    'addon-5-seokey-m2m': {
      code: 'addon-5-seokey-m2m',
      name: 'SEO Keyword',
      unitAmount: keywordPrice,
      quantity: 0,
    },
  }

  const seoPowerBoost =
    selectedAddons.find(a => a.code === 'addon-5-seopb-m2m') ||
    defaultAddons['addon-5-seopb-m2m']
  const seoKeyword =
    selectedAddons.find(a => a.code === 'addon-5-seokey-m2m') ||
    defaultAddons['addon-5-seokey-m2m']
  const seoPowerBoostDefault =
    selectedDefaultAddons.find(a => a.code === 'addon-5-seopb-m2m') ||
    defaultAddons['addon-5-seopb-m2m']
  const seoKeywordDefault =
    selectedDefaultAddons.find(a => a.code === 'addon-5-seokey-m2m') ||
    defaultAddons['addon-5-seokey-m2m']

  const [seoPbQty, setSeoPbQty] = useState(alphaPowerboostCount)
  const [seoKeyQty, setSeoKeyQty] = useState(alphaKeyCount)

  // Capture the original Recurly quantities at mount as the floor for each stepper.
  // alphaPowerboostCount/alphaKeyCount are overwritten by the useEffects below every
  // time the slider moves, so using the Redux selector directly as min creates a
  // ratchet (can never decrease). The ref stays fixed at the Recurly value.
  const minPbRef = useRef(alphaPowerboostCount)
  const minKeyRef = useRef(alphaKeyCount)

  // useEffect(() => {
  //   setSeoPbQty(seoPowerBoost.quantity || 0)
  //   setSeoKeyQty(seoKeyword.quantity || 0)
  // }, [selectedItem])

  useEffect(() => {
    dispatch(setAlphaKeyCount(seoKeyQty))
  }, [seoKeyQty])

  useEffect(() => {
    dispatch(setAlphaPowerBoostCount(seoPbQty))
  }, [seoPbQty])

  const buildAddon = (defaultAddon, addon, qty) => {
    const amount = qty * (addon.unitAmount || 0)
    let updatedAmount = amount
    if (defaultAddon && defaultAddon?.quantity > 0) {
      updatedAmount =
        defaultAddon?.amount < amount
          ? amount - defaultAddon?.amount
          : defaultAddon?.amount
    }

    let updatedAddon = {
      code: addon?.code,
      name: addon?.name,
      unitAmount: addon?.unitAmount,
      quantity: qty,
      amount: updatedAmount,
      // isAlreadyAdded: false,
      isUpgraded: addon?.isAlreadyAdded,
    }
    if (defaultAddon?.amount === amount || defaultAddon?.amount > amount) {
      updatedAddon = defaultAddon
    }
    return updatedAddon
  }

  useEffect(() => {
    const updatedAddons = []

    // If qty > 0 → include addon
    if (seoPbQty > 0)
      updatedAddons.push(
        buildAddon(seoPowerBoostDefault, seoPowerBoost, seoPbQty),
      )
    if (seoKeyQty > 0)
      updatedAddons.push(buildAddon(seoKeywordDefault, seoKeyword, seoKeyQty))

    onSEOAddonChange(updatedAddons)
  }, [seoPbQty, seoKeyQty])

  return (
    <KPSEOAddonWrapperStyle>
      <KPSEOStepper
        label="AEO & SEO PowerBoost"
        value={seoPbQty}
        min={minPbRef.current}
        max={3}
        step={1}
        onChange={setSeoPbQty}
        unitLabel={`$${seoPowerBoost.unitAmount} each`}
        disabled={disabled}
        defaultDisableValue={seoPowerBoostDefault.quantity}
      />

      <KPSEOStepper
        label="SEO Keyword"
        value={seoKeyQty}
        min={minKeyRef.current}
        max={5}
        step={1}
        onChange={setSeoKeyQty}
        unitLabel={`$${seoKeyword.unitAmount} each`}
        disabled={disabled}
        defaultDisableValue={seoKeywordDefault.quantity}
      />
    </KPSEOAddonWrapperStyle>
  )
}
